import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";

/**
 * Shared lookup row from the checked-in trailers query.
 * Mirrors what `CheckedInTrailerCombobox` selects so React Query dedupes.
 */
interface CheckedInTrailerLookup {
  id: string;
  trailer_id: string;
  trailer_type_id: string | null;
  load_status: string | null;
  current_location: { short_code: string } | null;
}

/**
 * Resolves a typed/selected `trailer_id` against the tenant's checked-in trailers.
 * Reuses the same query key as `CheckedInTrailerCombobox` so we don't double-fetch.
 */
export function useResolvedCheckedInTrailer(trailerId: string) {
  const { tenantId } = useTenant();
  const { data: trailerTypes } = useTrailerTypes();

  const { data: trailers = [], isLoading } = useQuery({
    queryKey: ["checked-in-trailers", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailers")
        .select(
          "id, trailer_id, trailer_type_id, load_status, current_location:locations!trailers_current_location_id_fkey(short_code)",
        )
        .eq("tenant_id", tenantId!)
        .eq("current_location_status", "at_site")
        .not("current_location_id", "is", null)
        .order("trailer_id");
      if (error) throw error;
      return (data ?? []) as unknown as CheckedInTrailerLookup[];
    },
    enabled: !!tenantId,
  });

  const normalized = trailerId.trim().toLowerCase();
  const match = normalized
    ? trailers.find((t) => t.trailer_id.toLowerCase() === normalized) ?? null
    : null;

  const inheritedTypeId = match?.trailer_type_id ?? null;
  const inheritedTypeLabel = getTrailerTypeLabel(inheritedTypeId, trailerTypes);

  return {
    matched: !!match,
    matchedTrailerRowId: match?.id ?? null,
    inheritedTypeId,
    inheritedTypeLabel,
    isLoading,
  };
}

/**
 * Trailer Type field is always shown and now always editable, even when the
 * trailer_id resolves to a checked-in trailer with a known type. The form
 * intercepts overrides via a confirmation dialog and propagates the new
 * value to the underlying `trailers` row on submit.
 *
 * Kept as a function (rather than inlining `false`) so call sites stay
 * uniform and we can re-introduce read-only behavior in one place if needed.
 */
export function isTrailerTypeReadOnly(_args: {
  trailerId: string;
  matched: boolean;
  inheritedTypeId: string | null;
}): boolean {
  return false;
}

/**
 * Effect helper: when a checked-in trailer with a known type is selected,
 * force `formData.trailer_type_id` to the inherited value (overwrite user's
 * prior choice — inheritance wins, per the product rule).
 */
export function useSyncInheritedTrailerType(
  inheritedTypeId: string | null,
  matched: boolean,
  currentTrailerTypeId: string,
  setTrailerTypeId: (id: string) => void,
  overrideActive: boolean = false,
) {
  useEffect(() => {
    if (overrideActive) return;
    if (matched && inheritedTypeId && currentTrailerTypeId !== inheritedTypeId) {
      setTrailerTypeId(inheritedTypeId);
    }
  }, [matched, inheritedTypeId, currentTrailerTypeId, setTrailerTypeId, overrideActive]);
}
/**
 * Default trailer type (trailer_types.slug) for a custom trailer — a typed ID
 * with no checked-in match — keyed by the departing location's short_code.
 * Applies per leg: each leg is checked against its own Ship From.
 */
export const CUSTOM_TRAILER_DEFAULT_SLUG_BY_ORIGIN: Record<string, string> = {
  "HTSH-DAL-01": "53_reefer",
};

export function customTrailerDefaultSlugFor(originShortCode: string | null | undefined): string | null {
  if (!originShortCode) return null;
  return CUSTOM_TRAILER_DEFAULT_SLUG_BY_ORIGIN[originShortCode.trim().toUpperCase()] ?? null;
}

/**
 * Effect helper: pre-fill the origin's default type once per newly typed
 * custom trailer ID. A trailer equal to the saved baseline never counts, so
 * opening an existing order never rewrites its stored type. Returns whether
 * the "please confirm" reminder should show.
 */
export function useCustomTrailerDefault(args: {
  trailerId: string;
  baselineTrailerId: string;
  matched: boolean;
  rosterLoading: boolean;
  defaultTypeId: string | null;
  currentTypeId: string;
  setTypeId: (id: string) => void;
}): boolean {
  const { trailerId, baselineTrailerId, matched, rosterLoading, defaultTypeId, currentTypeId, setTypeId } = args;
  const trimmed = trailerId.trim();
  const isNewCustom = !!trimmed && trimmed !== baselineTrailerId.trim() && !matched && !rosterLoading;
  const lastRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isNewCustom || !defaultTypeId) return;
    if (lastRef.current === trimmed) return;
    lastRef.current = trimmed;
    setTypeId(defaultTypeId);
  }, [isNewCustom, defaultTypeId, trimmed, setTypeId]);
  return isNewCustom && !!defaultTypeId && currentTypeId === defaultTypeId;
}
